import {
  tenantApiRoutes,
  type CreateUserDto,
  type LoginUserDto,
  type UpdateUserDto,
  type UserResponse,
} from '@repo/contracts/tenant-api';

import { tenantApi } from '@/redux/api/tenant-api';

/**
 * RTK Query API slice for users endpoints
 */
export const usersApi = tenantApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    /**
     * GET /users - List all users
     */
    getAllUsers: builder.query<UserResponse[], void>({
      query: () => tenantApiRoutes.users.list(),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * GET /users/me - Get current user
     */
    getCurrentUser: builder.query<UserResponse, void>({
      query: () => tenantApiRoutes.users.me(),
      providesTags: ['User'],
    }),

    /**
     * GET /users/organization - Get users for current organization
     */
    getOrganizationUsers: builder.query<UserResponse[], void>({
      query: () => tenantApiRoutes.users.organization(),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: 'AIRLINE_LIST' },
            ]
          : [{ type: 'User', id: 'AIRLINE_LIST' }],
    }),

    /**
     * GET /users/:id - Get user by ID
     */
    getUserById: builder.query<UserResponse, string>({
      query: (id) => tenantApiRoutes.users.getById(id),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    /**
     * POST /users - Create new user
     */
    createUser: builder.mutation<UserResponse[], CreateUserDto>({
      query: (data) => ({
        url: tenantApiRoutes.users.create(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'AIRLINE_LIST' },
      ],
    }),

    /**
     * PATCH /users/:id - Update user
     */
    updateUser: builder.mutation<
      UserResponse,
      { id: string; data: UpdateUserDto }
    >({
      query: ({ id, data }) => ({
        url: tenantApiRoutes.users.update(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'AIRLINE_LIST' },
      ],
    }),

    /**
     * PATCH /users - Update current user (self)
     */
    updateMe: builder.mutation<UserResponse, UpdateUserDto>({
      query: (data) => ({
        url: tenantApiRoutes.users.updateMe(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    /**
     * DELETE /users/:id - Delete user
     */
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: tenantApiRoutes.users.delete(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'AIRLINE_LIST' },
      ],
    }),

    /**
     * POST /users/login - Login user
     */
    login: builder.mutation<UserResponse, LoginUserDto>({
      query: (credentials) => ({
        url: tenantApiRoutes.users.login(),
        method: 'POST',
        body: credentials,
      }),
    }),

    /**
     * POST /users/:id/resend-invitation - Resend invitation email
     */
    resendInvitation: builder.mutation<UserResponse, string>({
      query: (id) => ({
        url: tenantApiRoutes.users.resendInvitation(id),
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetAllUsersQuery,
  useGetCurrentUserQuery,
  useGetOrganizationUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateMeMutation,
  useDeleteUserMutation,
  useLoginMutation,
  useResendInvitationMutation,
} = usersApi;

// Re-export types from contracts for convenience
export type { CreateUserDto, LoginUserDto, UpdateUserDto, UserResponse };
